import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import allRoutes from "client/constants/allRoutes";
import { useSelector } from "react-redux";

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = useSelector((state) => state.auth.permissions) || [];
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.RoleId === 1 || user?.RoleId === 2;
  const [moreOpen, setMoreOpen] = useState(false);

  const hasBilling = isAdmin || permissions.includes("view_finance") || permissions.includes("view_income_expense");

  // Check if current path matches any billing route
  const isBillingActive = [allRoutes.invoice, allRoutes.income, allRoutes.expense, allRoutes.billingPricingPlans, allRoutes.attendanceManagement, allRoutes.billingSettlement]
    .some((r) => location.pathname.startsWith(r));

  const isMoreActive = location.pathname.includes("profile") ||
    location.pathname.includes("settings") ||
    location.pathname.includes("predictive-intelligence") ||
    location.pathname.includes("therapy-intelligence") ||
    location.pathname.startsWith(allRoutes.employeelist);

  const tabClass = (isActive) =>
    `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-medium transition-colors ${
      isActive ? "text-brand-500" : "text-content-tertiary"
    }`;

  const moreItems = [
    { label: "Staff", path: allRoutes.employeelist, permission: "view_employees" },
    // The page opens on view_intelligence (Router.js); view_analytics alone was a link that bounced.
    { label: "Insights", path: allRoutes.predictiveIntelligence, permission: "view_intelligence" },
    { label: "Settings", path: allRoutes.settings },
  ].filter((item) => !item.permission || isAdmin || permissions.includes(item.permission));

  return (
    <>
      {/* More menu overlay */}
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-[90] bg-black/20"
            onClick={() => setMoreOpen(false)}
          />
          <div className="fixed bottom-[56px] right-3 z-[91] bg-surface-card rounded-xl shadow-elevation-3 border border-surface-border py-1 min-w-[160px] animate-fadeIn">
            {moreItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  setMoreOpen(false);
                  navigate(item.path);
                }}
                className={`w-full text-left px-4 py-2.5 text-xs font-medium transition-colors ${
                  location.pathname.startsWith(item.path)
                    ? "text-brand-500 bg-brand-50"
                    : "text-content-secondary hover:bg-surface-hover"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Bottom Navigation Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-[80] bg-surface-card border-t border-surface-border flex items-center lg:hidden"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Dashboard */}
        <NavLink
          to={allRoutes.portal}
          end
          className={({ isActive }) => tabClass(isActive)}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" />
          </svg>
          <span>Home</span>
        </NavLink>

        {/* Appointments */}
        <NavLink
          to={allRoutes.appointment}
          className={({ isActive }) => tabClass(isActive)}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>Appointments</span>
        </NavLink>

        {/* Patients */}
        <NavLink
          to={allRoutes.allpatient}
          className={({ isActive }) => tabClass(isActive)}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>Patients</span>
        </NavLink>

        {/* Billing (if access) */}
        {hasBilling && (
          <button
            onClick={() => navigate(allRoutes.invoice)}
            className={tabClass(isBillingActive)}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
            <span>Billing</span>
          </button>
        )}

        {/* More */}
        <button
          onClick={() => setMoreOpen((prev) => !prev)}
          className={tabClass(isMoreActive || moreOpen)}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
          </svg>
          <span>More</span>
        </button>
      </nav>
    </>
  );
};

export default MobileBottomNav;
